import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Lightning, Phone as PhoneIcon, Lock, GoogleLogo } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(phone, password);
      toast.success("Bienvenue chez BKPRO");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex flex-col relative overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#005CFF]/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#005CFF]/10 blur-3xl" />

      <div className="relative flex-1 flex flex-col justify-between px-6 pt-safe pb-8 max-w-md mx-auto w-full">
        <div className="pt-16">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_30px_rgba(0,92,255,0.5)]">
              <Lightning size={24} weight="fill" color="white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl tracking-tight leading-none">BKPRO</div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 mt-0.5">Chantier</div>
            </div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-10 leading-[1.05]">
            Le terrain,<br />piloté au doigt.
          </h1>
          <p className="text-white/60 mt-4 text-sm leading-relaxed">
            Planning, chantiers, signatures & rapports — pour BKPRO Électricité, sur mobile.
          </p>
        </div>

        <div className="space-y-4 mt-10">
          <Button
            type="button"
            onClick={startGoogleLogin}
            className="w-full h-14 rounded-2xl bg-white text-[#0A0A0A] hover:bg-white/90 font-semibold text-base shadow-[0_10px_40px_-10px_rgba(255,255,255,0.35)] active:scale-[0.98] transition-transform flex items-center justify-center gap-3"
            data-testid="login-google-button"
          >
            <GoogleLogo size={20} weight="bold" />
            Continuer avec Google
          </Button>

          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/40">
            <div className="flex-1 h-px bg-white/10" />
            <span>ou par téléphone</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white/70 text-xs uppercase tracking-widest">Téléphone</Label>
              <div className="relative">
                <PhoneIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="0699999999"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-14 pl-11 pr-4 rounded-2xl bg-white/5 border-white/10 text-white text-base placeholder:text-white/30 focus-visible:ring-[#005CFF]"
                  data-testid="login-phone-input"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70 text-xs uppercase tracking-widest">Mot de passe</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 pl-11 pr-4 rounded-2xl bg-white/5 border-white/10 text-white text-base placeholder:text-white/30 focus-visible:ring-[#005CFF]"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-2xl bg-[#005CFF] hover:bg-[#0047CC] text-white font-semibold text-base shadow-[0_10px_40px_-10px_rgba(0,92,255,0.7)] active:scale-[0.98] transition-transform"
              data-testid="login-submit-button"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
